import { useRef, useState } from 'react';
import type { DragEvent } from 'react';

// Lightweight HTML5 drag-to-reorder for a list. Use a drag HANDLE (handleProps)
// so it doesn't fight text selection inside row inputs; the row is the drop
// target (rowProps). Zero dependencies.
export function useDragReorder<T>(items: T[], onChange: (next: T[]) => void) {
  const from = useRef<number | null>(null);
  const [over, setOver] = useState<number | null>(null);

  const move = (a: number, b: number) => {
    if (a === b) return;
    const next = items.slice();
    const [m] = next.splice(a, 1);
    next.splice(b, 0, m);
    onChange(next);
  };

  const handleProps = (i: number) => ({
    draggable: true,
    onDragStart: (e: DragEvent) => { from.current = i; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); },
    onDragEnd: () => { from.current = null; setOver(null); },
    style: { cursor: 'grab' as const },
  });

  const rowProps = (i: number) => ({
    onDragOver: (e: DragEvent) => { if (from.current !== null) { e.preventDefault(); if (over !== i) setOver(i); } },
    onDrop: (e: DragEvent) => { e.preventDefault(); if (from.current !== null) move(from.current, i); from.current = null; setOver(null); },
    isOver: over === i,
  });

  return { handleProps, rowProps, draggingIndex: from.current };
}
